import { useEffect, useMemo } from 'react'

import { T } from 'govwell-ui'

import { navigate, routes } from '@cedarjs/router'
import { Metadata } from '@cedarjs/web'

import InspectionHoursDisplay from 'src/components/InspectionHoursCalendar/InspectionHoursDisplay'
import Page from 'src/components/shared/Page/Page'
import { LoadingSpin } from 'src/components/shared/StyledComponents'
import { useOrganizationHomePageQuery } from 'src/fetch/organizationHomePage'
import TopbarLayout from 'src/layouts/TopbarLayout/TopbarLayout'
import { DateFormats, formatDateInTimeZone } from 'src/utils/date'
import { useIsInspectionCalendarExternalIntegrationEnabled } from 'src/utils/featureFlagging/featureFlagging'

import ScheduledHolidaysSection from './ScheduledHolidaysSection'

interface InspectionInformationPageProps {
  slug: string
}

const InspectionInformationPage = ({ slug }: InspectionInformationPageProps) => {
  const isCalendarIntegrationEnabled = useIsInspectionCalendarExternalIntegrationEnabled()
  const { organization, isLoading } = useOrganizationHomePageQuery(slug)

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (
      !organization ||
      !organization.shouldExposeInspectionAvailabilityInformationToPublic ||
      !isCalendarIntegrationEnabled
    ) {
      navigate(routes.cityHome({ slug }), { replace: true })
    }
  }, [isCalendarIntegrationEnabled, isLoading, organization, slug])

  const city = organization?.city
  const displayName = organization?.displayName
  const recordSettings = organization?.recordSettings
  const recordTypes = organization?.recordTypes
  const timeZone = organization?.timeZone

  const pageTitle = displayName
    ? `${displayName} Inspection Information`
    : `${city} Inspection Information`

  const organizationHours = (recordSettings?.inspectionAvailabilityWindows ?? []).filter(
    (window): window is NonNullable<typeof window> => !!window
  )

  const inspectionEnabledModules = useMemo(
    () =>
      (recordTypes ?? []).filter(
        (recordType): recordType is NonNullable<typeof recordTypes>[number] =>
          !!recordType?.allowInspections
      ),
    [recordTypes]
  )

  if (isLoading) {
    return <LoadingSpin />
  }

  if (!organization || !organization.shouldExposeInspectionAvailabilityInformationToPublic) {
    return null
  }

  const formatCutoffTime = (cutoff: string | null | undefined) => {
    if (!cutoff) {
      return 'No cutoff time'
    }
    return formatDateInTimeZone(cutoff, timeZone, DateFormats.TimeWithAMPMLowercase)
  }

  return (
    <TopbarLayout>
      <Metadata title={pageTitle} />
      <Page>
        <Page.Header
          title="Inspection Information"
          subtitle="View inspection hours, cutoff times, and scheduled holidays."
          backButtonHref={routes.cityHome({ slug })}
        />
        <Page.Content>
          <div className="flex w-full flex-col gap-8">
            <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
              <T.H3>Organization Inspection Hours</T.H3>
              {displayName && (
                <T.BodySmall className="mb-4">
                  Standard inspection hours for {displayName}.
                </T.BodySmall>
              )}
              <InspectionHoursDisplay
                windows={organizationHours}
                closedLabel="Closed"
                type="tiles"
              />
            </section>

            {inspectionEnabledModules.length > 0 && (
              <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
                <T.H3>Inspection Request Cutoff Times</T.H3>
                <T.BodySmall className="mb-4">
                  Different inspections have varying cutoff times. You must request an inspection
                  before the specified time; otherwise, it will be scheduled for the next business
                  day.
                </T.BodySmall>
                <div className="mt-4 grid gap-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {inspectionEnabledModules.map((recordType) => (
                    <div
                      key={recordType.id}
                      className="flex flex-col gap-1 rounded-md border border-neutral-200 bg-neutral-50 p-4"
                    >
                      <T.H4>{recordType.displayName}</T.H4>
                      <T.Body>
                        {formatCutoffTime(recordType.recordSettings?.inspectionCutoffTime)}
                      </T.Body>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <ScheduledHolidaysSection orgSlug={slug} />
          </div>
        </Page.Content>
      </Page>
    </TopbarLayout>
  )
}

export default InspectionInformationPage
